import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UsersService } from '../users.service';
import { InventoryService } from '../../inventory/inventory.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { User } from '../user.model';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-users-create-edit',
  templateUrl: './users-create-edit.component.html',
  styleUrls: ['./users-create-edit.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, NgSelectModule],
})
export class UsersCreateEditComponent implements OnInit {
  userForm: FormGroup;
  isEdit = false;
  userId: string | null = null;
  stores: any[] = [];
  roles: any[] = [];

  /** Which credential boxes are currently revealed (the eye toggles). */
  show: Record<'password' | 'confirm' | 'pin', boolean> = {
    password: false,
    confirm: false,
    pin: false,
  };

  toggle(field: 'password' | 'confirm' | 'pin'): void {
    this.show[field] = !this.show[field];
  }

  constructor(
    private fb: FormBuilder,
    private usersService: UsersService,
    private inventoryService: InventoryService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
  ) {
    this.userForm = this.fb.group(
      {
        username: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        mobile: ['', Validators.required],
        password: ['', Validators.required],
        confirmPassword: ['', Validators.required],
        pin: ['', Validators.required],
        name: ['', Validators.required],
        roleIds: [null, Validators.required],
        storeIds: [[], Validators.required],

        isActive: [true],
        clientId: [''],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  // Custom validator to check if password and confirmPassword match.
  // In edit mode, both blank means "don't change password" — that's valid.
  // If one is filled, the other must be filled too and they must match.
  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value || '';
    const confirmPassword = form.get('confirmPassword')?.value || '';

    if (!password && !confirmPassword) {
      return null;
    }

    if (password && !confirmPassword) {
      return { confirmPasswordRequired: true };
    }

    if (!password && confirmPassword) {
      return { passwordRequired: true };
    }

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  ngOnInit(): void {
    this.loadStores();
    this.loadRoles();
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.userId;

    if (this.isEdit) {
      // Remove required validators for password and pin in edit mode
      this.userForm.get('password')?.clearValidators();
      this.userForm.get('confirmPassword')?.clearValidators();
      this.userForm.get('pin')?.clearValidators(); // 👈 Clear PIN validator

      this.userForm.get('password')?.updateValueAndValidity();
      this.userForm.get('confirmPassword')?.updateValueAndValidity();
      this.userForm.get('pin')?.updateValueAndValidity(); // 👈 Update PIN validity
    }

    if (this.isEdit && this.userId) {
      this.usersService.getUser(this.userId).subscribe((res) => {
        const user = res.user; // Extract user from response object
        this.userForm.patchValue({
          username: user.username,
          email: user.email,
          mobile: user.mobile,
          // password and pin left blank — backend stores them hashed, user
          // must re-enter them to change. Leaving blank keeps existing values.
          name: user.name,
          // UserRole is many-to-many — keep EVERY role the user holds. Taking
          // roles[0] silently dropped the rest on the next save.
          roleIds: (user.roles ?? [])
            .map((r: any) => r?.role?.id || r?.roleId)
            .filter(Boolean),
          // Map UserStoreAccess array to storeIds array for multi-select
          storeIds:
            user.UserStoreAccess && user.UserStoreAccess.length > 0
              ? user.UserStoreAccess.map((s: any) => s.storeId)
              : user.storeId
                ? [user.storeId]
                : [],
          isActive: user.isActive ?? true,
          clientId: user.clientId || '',
        });
      });
    }
  }

  submit(): void {
    if (this.userForm.invalid) {
      if (this.userForm.errors?.['passwordMismatch']) {
        this.toastr.error('Passwords do not match!');
      }
      return;
    }

    const formValue = this.userForm.value;
    const { confirmPassword, ...payloadData } = formValue;

    const payload: any = {
      ...payloadData,
      // The backend takes roleIds as an array (CreateUserDto.roleIds, one
      // UserRole row per id). The control is multi-select; the single-value
      // branch only covers a legacy patched value.
      roleIds: Array.isArray(formValue.roleIds)
        ? formValue.roleIds
        : formValue.roleIds
          ? [formValue.roleIds]
          : [],
      storeIds: formValue.storeIds || [],
    };

    if (this.isEdit) {
      // In edit mode, if password or pin is empty, don't send it
      if (!payload.password) {
        delete payload.password;
      }
      if (!payload.pin) {
        delete payload.pin;
      }
    }

    if (this.isEdit && this.userId) {
      this.usersService.updateUser(this.userId, payload).subscribe({
        next: () => {
          this.toastr.success('User updated successfully!');
          this.router.navigate(['/users']);
        },
        error: (err) => {
          this.toastr.error('Failed to update user. Please try again.');
          console.error('Update user error:', err);
        },
      });
    } else {
      this.usersService.createUser(payload).subscribe({
        next: () => {
          this.toastr.success('User created successfully!');
          this.router.navigate(['/users']);
        },
        error: (err) => {
          this.toastr.error('Failed to create user. Please try again.');
          console.error('Create user error:', err);
        },
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/users']);
  }

  loadStores(): void {
    this.inventoryService.getAllStores().subscribe({
      next: (res: any) => {
        this.stores = res?.data || res || [];
      },
      error: (err) => console.error('Failed to load stores', err),
    });
  }

  loadRoles(): void {
    this.usersService.getRoles().subscribe({
      next: (res: any) => {
        this.roles = res?.data || res?.roles || res || [];
      },
      error: (err) => console.error('Failed to load roles', err),
    });
  }
}
